import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { addRecentLogin } from "../../util/recent-logins";
import {
  ASSIGN_RACKS,
  CHANGE_PASSWORD,
  CREATE_STATION,
  GET_ALL_ROLES,
  GET_ALL_USERS,
  GET_ASSIGNED_CONVEYORS,
  GET_ASSIGNED_VALIDATION,
  LOGIN_URL,
  LOGIN_USERS,
  SET_USER_ROLE,
  SIGNUP_URL,
  UPDATE_PERMISSIONS,
  UPDATE_ROLE_NAME,
  UPDATE_USER,
} from "../../configs";
import {
  SET_USER_ACCESS_CODE,
  SET_USER_NFC_CARD,
  GET_ALL_PERMISSIONS,
  CREATE_PERMISSION,
  DELETE_PERMISSION,
  LOCK_USER_ACCOUNT,
  UNLOCK_USER_ACCOUNT,
  RESET_UNLOCK_ATTEMPTS,
  VALIDATE_SESSION,
  REVOKE_USER_SESSION,
} from "../../configs/apiConfig";
import configureAxios from "../../configs/axiosConfig";
import {
  AssignConveyorsRequest,
  AssignedConveyor,
  GetAssignedConveyorsRequest,
  ILoginRequest,
  ISignUpRequest,
  ISignUpResponse,
  IUserResponse,
  PaginatedResponse,
} from "../../interfaces";
import { ILoginResponse, ITokenResponse } from "../../interfaces";
import { IChangePasswordRequest } from "../../interfaces/requests/ChangePasswordRequest";
import { IChangeePasswordResponse } from "../../interfaces/responces/ChangePasswordResponse";
import { BaseResponse, BaseResponseSingle } from "../../typings";
import {
  IGetAllUserResponse,
  LoginUser,
  Role,
} from "../../typings/interfaces/responses/LoginUser";

export const loginAction = createAsyncThunk(
  "auth/login",
  async (loginData: ILoginRequest, { rejectWithValue }) => {
    try {
      const response = await axios.post<ILoginResponse>(LOGIN_URL, loginData);
      const body = response.data.body;
      let token: ITokenResponse | null = null;
      let user: IUserResponse | null = null;
      if (body !== null) {
        token = body[0] as ITokenResponse;
        axios.defaults.headers.common[
          "Authorization"
        ] = `Bearer ${token.token}`;
        user = body[1] as IUserResponse;
        localStorage.setItem("user", JSON.stringify(user));
        localStorage.setItem("token", token.token);
        localStorage.removeItem("profile_lock");
        addRecentLogin(user.firstName, user.lastName, user.email);
      }
      return { user, token, response: response.data };
    } catch (err) {
      if (!err) {
        throw err;
      }
      return rejectWithValue(err);
    }
  }
);

export const signupAction = createAsyncThunk(
  "auth/signup",
  async (signupdata: ISignUpRequest, { rejectWithValue }) => {
    try {
      const response = await axios.post<ISignUpResponse>(
        SIGNUP_URL,
        signupdata
      );

      return response.data;
    } catch (err) {
      if (!err) {
        throw err;
      }
      return rejectWithValue(err);
    }
  }
);

export const logoutAction = createAsyncThunk(
  "auth/logout",
  async (_, { rejectWithValue }) => {
    try {
      // Only clear auth-specific keys — "cashier" (device station grant),
      // "activeCashierSession"/"stationAccessSessionId" (open register state),
      // and other station/device-level keys must survive a logout since they
      // describe the physical station, not the logged-in user.
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      localStorage.removeItem("profile_lock");
      delete axios.defaults.headers.common["Authorization"];
    } catch (err) { }
  }
);

export const lockProfileAction = createAsyncThunk(
  "auth/lock",
  async (_, { rejectWithValue }) => {
    try {
      localStorage.setItem("profile_lock", "true");
    } catch (err) { }
  }
);

export const unlockProfileAction = createAsyncThunk(
  "auth/unlock",
  async (_, { rejectWithValue }) => {
    try {
      localStorage.removeItem("profile_lock");
    } catch (err) { }
  }
);

export const authDetailsInitializeAction = createAsyncThunk(
  "auth/initialize",
  async (_, { rejectWithValue }) => {
    try {
      const storedToken = localStorage.getItem("token");
      const storedUserData = localStorage.getItem("user");
      const profileLock = localStorage.getItem("profile_lock") === "true";

      if (storedToken == null) {
        return { storedToken: null, user: null, userAvailable: false, profileLock, accountLocked: false, sessionRevoked: false };
      }

      configureAxios(storedToken);

      // Validate the session against the server — detects revocation and account lock
      try {
        const validateResponse = await axios.get(VALIDATE_SESSION);
        const body = validateResponse.data;

        if (body?.status === "revoked" || body?.status === "invalid") {
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          return { storedToken: null, user: null, userAvailable: false, profileLock: false, accountLocked: false, sessionRevoked: true };
        }

        const freshUser: IUserResponse = body?.body ?? JSON.parse(storedUserData || "null");
        const accountLocked = freshUser ? (freshUser as any).isLocked === true : false;

        if (freshUser) {
          localStorage.setItem("user", JSON.stringify(freshUser));
        }

        return {
          storedToken,
          user: freshUser,
          userAvailable: freshUser !== null,
          profileLock,
          accountLocked,
          sessionRevoked: false,
        };
      } catch {
        // Backend unreachable — restore from localStorage without blocking
        const user = JSON.parse(storedUserData || "null") as IUserResponse | null;
        return { storedToken, user, userAvailable: user !== null, profileLock, accountLocked: false, sessionRevoked: false };
      }
    } catch (err) {
      if (!err) {
        throw err;
      }
      return rejectWithValue(err);
    }
  }
);

export const changePasswordAction = createAsyncThunk(
  "auth/password/change",
  async (changeData: IChangePasswordRequest, { rejectWithValue }) => {
    try {
      const response = await axios.post<IChangeePasswordResponse>(
        CHANGE_PASSWORD,
        changeData
      );
      const body = response.data.body;
      let token: ITokenResponse | null = null;
      let user: IUserResponse | null = null;
      if (body !== null) {
        token = body[0] as ITokenResponse;
        axios.defaults.headers.common[
          "Authorization"
        ] = `Bearer ${token.token}`;
        user = body[1] as IUserResponse;
        localStorage.setItem("user", JSON.stringify(user));
        localStorage.setItem("token", token.token);
      }
      return { user, token, response: response.data };
    } catch (err) {
      if (!err) {
        throw err;
      }
      return rejectWithValue(err);
    }
  }
);

export const getLoginUserList = createAsyncThunk(
  "auth/getLoginUserList",
  async (tenant: string, { rejectWithValue }) => {
    try {
      const response = await axios.get<BaseResponse<LoginUser>>(
        LOGIN_USERS + "?tenant=" + tenant
      );
      return response.data.body;
    } catch (err) { }
  }
);

export const getAllUsers = createAsyncThunk(
  "auth/getallusers",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get<IGetAllUserResponse[]>(GET_ALL_USERS);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || err.message || "Failed to fetch users"
      );
    }
  }
);

export const getAllRoles = createAsyncThunk(
  "auth/getallroles",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get<BaseResponse<Role>>(GET_ALL_ROLES);
      console.log(response.data);
      return response.data.body;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || err.message || "Failed to fetch roles"
      );
    }
  }
);

export const createRoleAction = createAsyncThunk(
  "auth/createRole",
  async (
    { name, description }: { name: string; description: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await axios.post(GET_ALL_ROLES, { name, description });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || err.message || "Failed to create role"
      );
    }
  }
);

export const updateRoleAction = createAsyncThunk(
  "auth/updateRole",
  async (
    roleData: { roleId: number; name: string; description: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await axios.patch(UPDATE_ROLE_NAME(roleData.roleId), {
        name: roleData.name,
        description: roleData.description,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || err.message || "Failed to update role"
      );
    }
  }
);

export const updateUserAction = createAsyncThunk(
  "auth/updateUser",
  async (userdata: ISignUpRequest, { rejectWithValue }) => {
    try {
      const response = await axios.put<ISignUpResponse>(
        UPDATE_USER(userdata.id ?? 0),
        userdata
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || err.message || "Failed to update user"
      );
    }
  }
);

export const updatePermissionsAction = createAsyncThunk(
  "auth/updatePermissions",
  async (
    { roleId, permissions }: { roleId: number; permissions: string[] },
    thunkAPI
  ) => {
    try {
      const res = await axios.put(UPDATE_PERMISSIONS(roleId), permissions);
      return res.data;
    } catch (err: any) {
      return thunkAPI.rejectWithValue(
        err.response?.data?.message || "Failed to update permissions"
      );
    }
  }
);

export const setUserRoleAction = createAsyncThunk(
  "auth/setUserRole",
  async ({ userId, roleId }: { userId: number; roleId: number }, thunkAPI) => {
    try {
      const res = await axios.put(SET_USER_ROLE, { userId, roleId });
      return res.data;
    } catch (err: any) {
      return thunkAPI.rejectWithValue(
        err.response?.data?.message || "Failed to set user role"
      );
    }
  }
);

export const addNewStationAction = createAsyncThunk(
  "auth/addNewStation",
  async (
    {
      storeId,
      merchantId,
      deviceName,
    }: { storeId: number; merchantId: number; deviceName: string },
    thunkAPI
  ) => {
    try {
      const res = await axios.post(CREATE_STATION, {
        storeId,
        merchantId,
        deviceName,
      });
      return res.data;
    } catch (err: any) {
      return thunkAPI.rejectWithValue(
        err.response?.data?.message || "Failed to add new station"
      );
    }
  }
);

export const getAllPermissionsAction = createAsyncThunk(
  "auth/getAllPermissions",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(GET_ALL_PERMISSIONS);
      return response.data.body;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || "Failed to fetch permissions");
    }
  }
);

export const createPermissionAction = createAsyncThunk(
  "auth/createPermission",
  async ({ name, description }: { name: string; description: string }, { rejectWithValue }) => {
    try {
      const response = await axios.post(CREATE_PERMISSION, { name, description });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || "Failed to create permission");
    }
  }
);

export const deletePermissionAction = createAsyncThunk(
  "auth/deletePermission",
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await axios.delete(DELETE_PERMISSION(id));
      return { id, response: response.data };
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || "Failed to delete permission");
    }
  }
);

export const setAccessCodeAction = createAsyncThunk(
  "auth/setAccessCode",
  async (
    { userId, accessCode }: { userId: number; accessCode: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await axios.put(SET_USER_ACCESS_CODE(userId), {
        accessCode,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || err.message || "Failed to set access code"
      );
    }
  }
);

export const setNfcCardAction = createAsyncThunk(
  "auth/setNfcCard",
  async (
    { userId, nfcCardId }: { userId: number; nfcCardId: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await axios.put(SET_USER_NFC_CARD(userId), { nfcCardId });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || err.message || "Failed to assign NFC card"
      );
    }
  }
);

export const getAssignedConveyorsAction = createAsyncThunk<
  AssignedConveyor[],
  GetAssignedConveyorsRequest
>("assignedConveyors/get", async (params, { rejectWithValue }) => {
  try {
    const url = GET_ASSIGNED_CONVEYORS(
      params.conveyorId,
      params.slotId,
      params.billNumber,
      params.date,
      params.page,
      params.size
    );

    const response = await axios.get<
      BaseResponseSingle<PaginatedResponse<AssignedConveyor>>
    >(url);

    return response.data.body.content;
  } catch (err: any) {
    return rejectWithValue(
      err.response?.data?.message ||
      err.message ||
      "Failed to fetch assigned conveyors"
    );
  }
});

export const assignConveyorsAction = createAsyncThunk<
  string,
  AssignConveyorsRequest,
  { rejectValue: string }
>("assignedConveyors/assign", async (requestData, { rejectWithValue }) => {
  try {
    const response = await axios.post<BaseResponseSingle<string>>(
      ASSIGN_RACKS,
      requestData
    );

    // The API answers 200 even when the assignment fails, so axios never
    // rejects on its own — the envelope status is the only reliable signal.
    if (response.data?.status !== "SUCCESS") {
      return rejectWithValue(
        response.data?.message || "Failed to assign conveyors"
      );
    }

    return response.data.body;
  } catch (err: any) {
    return rejectWithValue(
      err.response?.data?.message || err.message || "Failed to assign conveyors"
    );
  }
});

export const lockUserAccountAction = createAsyncThunk(
  "auth/lockUserAccount",
  async (userId: number, { rejectWithValue }) => {
    try {
      const response = await axios.put(LOCK_USER_ACCOUNT(userId));
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || err.message || "Failed to lock account"
      );
    }
  }
);

export const unlockUserAccountAction = createAsyncThunk(
  "auth/unlockUserAccount",
  async (
    { userId, managerId, enteredCode, nfcCardId }: { userId: number; managerId?: number; enteredCode?: string; nfcCardId?: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await axios.put(UNLOCK_USER_ACCOUNT(userId), {
        managerId: managerId != null ? String(managerId) : undefined,
        enteredCode,
        nfcCardId,
      });
      if (response.data?.status !== "SUCCESS") {
        return rejectWithValue(response.data?.message || "Failed to unlock account");
      }
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || err.message || "Failed to unlock account"
      );
    }
  }
);

export const resetUnlockAttemptsAction = createAsyncThunk(
  "auth/resetUnlockAttempts",
  async (userId: number, { rejectWithValue }) => {
    try {
      const response = await axios.put(RESET_UNLOCK_ATTEMPTS(userId));
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || err.message || "Failed to reset unlock attempts"
      );
    }
  }
);

export const revokeUserSessionAction = createAsyncThunk(
  "auth/revokeUserSession",
  async (userId: number, { rejectWithValue }) => {
    try {
      const response = await axios.delete(REVOKE_USER_SESSION(userId));
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || err.message || "Failed to revoke session"
      );
    }
  }
);

export const validateAssignedConveyorAction = createAsyncThunk<
  // `body` is a boolean conflict flag, not a conveyor record.
  BaseResponseSingle<boolean>, // fulfilled value
  { slotKey: string; billId: string }, // arg
  { rejectValue: { message: string; statusCode?: number } } // reject value
>(
  "assignedConveyors/validate",
  async ({ slotKey, billId }, { rejectWithValue }) => {
    try {
      const url = GET_ASSIGNED_VALIDATION(slotKey, billId);
      const { data } = await axios.get<BaseResponseSingle<boolean>>(url);
      return data; // <- contains { body, message, statusCode }
    } catch (err: any) {
      return rejectWithValue({
        message:
          err?.response?.data?.message ??
          err?.message ??
          "Failed to validate assignment",
        statusCode: err?.response?.status ?? err?.response?.data?.statusCode,
      });
    }
  }
);
